import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {Toaster} from "@/components/ui/toaster";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import ProductDetail from "./components/ProductDetail/ProductDetail";
import MyAccount from "./components/MyAccount/MyAccount";
import CartPage from "./components/Cart/CartPage/CartPage";
import {AuthProvider} from "./components/Auth/AuthContext";
import {CartProvider} from "./components/Cart/CartContext";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import Checkout from "./components/Checkout/Checkout";
import MyOrdersInfo from "./components/MyAccount/MyOrdersInfo";
import MyOrderDetails from "./components/MyAccount/MyOrderDetails";
import OrdersValidation from "./components/Validation/OrdersValidation";
import MyPreOrdersInfo from "./components/MyAccount/MyPreOrdersInfo";
import MyPreOrderDetails from "./components/MyAccount/MyPreOrderDetails";
import MyShippingDetails from "./components/MyAccount/MyShippingDetails";
import CategoryList from "./components/Category/CategoryList";
import Auth from "./components/Auth/Auth";
import {GuardedRouteAuth} from "./GuardedRoute";
import InvoiceListPage from "@/components/MyAccount/InvoiceList/InvoiceListPage";
import ShippingCsvListPage from "./components/MyAccount/ShippingCsvList/ShippingCsvListPage";
import ClientManagement from "@/components/MyAccount/ClientManagement/ClientManagement";
import ClientManagementOrganization from "@/components/MyAccount/ClientManagement/ClientManagementOrganization";
import TransitArticles from "./components/TransitArticles/TransitArticles";
import {LanguageProvider} from "@/components/Navbar/LanguageContext";
import DeliveryNoteListPage from "@/components/MyAccount/DeliveryNoteListPage";
import DeliveryNoteDetails from "@/components/MyAccount/DeliveryNoteDetails";
import Login from "@/components/Login/Login";
import UserManagement from "@/components/MyAccount/UserManagement/UserManagement";
import ChangeUserPage from "@/components/MyAccount/UserManagement/ChangeUserPage";
import AddNewUserPage from "@/components/MyAccount/UserManagement/AddNewUserPage";
import ReturnOrders from "@/components/MyAccount/ReturnOrders"
import CincuPricesPage from "@/components/MyAccount/CincuPrices/CincuPricesPage";

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);

const router = createBrowserRouter([
    {
        path: "/",
        element: <GuardedRouteAuth
            component={App}
        />,
        errorElement: <ErrorPage/>,
    },
    {
        path: "/auth",
        element: <Auth/>,
        errorElement: <ErrorPage/>,
    },
    {
        path: "/product/:productId",
        element: <GuardedRouteAuth
            component={ProductDetail}
        />,
    },
    {
        path: "/my-account",
        element: <GuardedRouteAuth
            component={MyAccount}
        />,
    },
    {
        path: "/my-account/return-orders",
        element: <GuardedRouteAuth
            component={ReturnOrders}
        />,
    },
    {
        path: "/my-account/cincu-prices",
        element: <GuardedRouteAuth
            component={CincuPricesPage}
        />,
    },
    {
        path: "/login",
        element: <GuardedRouteAuth
            component={Login}
            reverse={true}
        />,
    },
    {
        path: "/my-account/info",
        element: <GuardedRouteAuth
            component={MyAccount}
        />,
    },
    {
        path: "/my-account/orders",
        element: <GuardedRouteAuth
            component={MyOrdersInfo}
        />,
    },
    {
        path: "/my-account/preorders",
        element: <GuardedRouteAuth

            component={MyPreOrdersInfo}
        />,
    },
    {
        path: "/my-account/orders/order-detail/:orderId",
        element: <GuardedRouteAuth

            component={MyOrderDetails}
        />,
    },
    {
        path: "/my-account/preorders/pre-order-detail/:preOrderId",
        element: <GuardedRouteAuth

            component={MyPreOrderDetails}
        />,
    },
    {
        path: "/my-account/invoices",
        element: <GuardedRouteAuth

            component={InvoiceListPage}
        />,
    },
    {
        path: "/my-account/delivery-note",
        element: <GuardedRouteAuth

            component={DeliveryNoteListPage}
        />,
    },
    {
        path: "/my-account/delivery-note/:deliveryNoteId",
        element: <GuardedRouteAuth

            component={DeliveryNoteDetails}
        />,
    },
    {
        path: "/my-account/shipping-csv",
        element: <GuardedRouteAuth

            component={ShippingCsvListPage}
        />,
    },
    {
        path: "/my-cart",
        element: <GuardedRouteAuth

            component={CartPage}
        />,
    },
    {
        path: "/checkout",
        element: <GuardedRouteAuth

            component={Checkout}
        />,
    },
    {
        path: "/validation/preorders",
        element: <GuardedRouteAuth

            component={OrdersValidation}
        />,
    },
    {
        path: "/my-account/shipping/:shippingId",
        element: <GuardedRouteAuth

            component={MyShippingDetails}
        />,
    },
    {
        path: "/category/:localMarketId",
        element: <GuardedRouteAuth

            component={CategoryList}
        />,
    },
    {
        path: "/client-management",
        element: <GuardedRouteAuth

            component={ClientManagement}
        />,
    },
    {
        path: "/user-management",
        element: <GuardedRouteAuth

            component={UserManagement}
        />,
    },
    {
        path: "/client-organization/:clientId",
        element: <GuardedRouteAuth

            component={ClientManagementOrganization}
        />,
    },
    {
        path: "/user-management/new-user",
        element: <GuardedRouteAuth

            component={AddNewUserPage}
        />,
    },
    {
        path: "/user-management/change-user/:userId",
        element: <GuardedRouteAuth

            component={ChangeUserPage}
        />,
    },
    {
        path: "/transit",
        element: <GuardedRouteAuth

            component={TransitArticles}
        />,
    },
]);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // Disable refetch when window gains focus (tab switching)
            refetchOnReconnect: false,   // Disable refetch when network reconnects
            refetchOnMount: false,       // Disable refetch when component mounts if data exists
            staleTime: 5 * 60 * 1000,    // Data stays fresh for 5 minutes
            retry: 1,                    // Only retry failed requests once
        },
    },
});

root.render(
    // <React.StrictMode>
    <QueryClientProvider client={queryClient}>

        <LanguageProvider>
            <AuthProvider>
                <CartProvider>
                    <Toaster/>
                    <RouterProvider router={router}/>
                    <ReactQueryDevtools initialIsOpen={false}/>
                </CartProvider>
            </AuthProvider>
        </LanguageProvider>
    </QueryClientProvider>
    // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
